import {useId} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon, EditIcon} from "@pg-design/icons-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {Switcher} from "@pg-design/switcher-module";
import {Text} from "@pg-design/text-module";
import {HOME_MATCH_BASIC_SUMMARY_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import type {IRegionListRegion} from "../../../search/actions/fetch_search_regions_action";
import {useHomeMatchBasicSummaryGtmTracking} from "../../hooks/use_home_match_basic_summary_gtm_tracking";
import type {HomeMatchBasicPreferencesBasis, IHomeMatchBasicPreferencesFlowTrackingContext} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import type {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {getHomeMatchBasicPreferencesBasisText, getHomeMatchBasicPreferencesSummaryText} from "../../utils/home_match_basic_preferences_flow_utils";

const buildingsImage = require("../../images/home_match_basic_buildings.svg");

const HOME_MATCH_BASIC_BENEFITS = [
    "Automatycznie wysyłamy zapytania do deweloperów w Twoim imieniu.",
    "Deweloperzy sami wracają do Ciebie z dopasowanymi ofertami.",
    "O najlepszych okazjach informujemy Cię od razu SMS-em i e-mailem."
];

interface IProps {
    isOpen: boolean;
    values: IPreferencesDefaultValues;
    preferencesBasis: HomeMatchBasicPreferencesBasis;
    regionDetails?: IRegionListRegion[];
    homeMatchBasicConsent: boolean;
    isSaving: boolean;
    onHomeMatchBasicConsentChange: (value: boolean) => void;
    onEditPreferences: () => void;
    onConfirm: () => void;
    onClose: () => void;
    trackingContext?: IHomeMatchBasicPreferencesFlowTrackingContext;
    zIndex?: number;
}

export const HomeMatchBasicPreferencesFlowSummaryResponsiveModal = (props: IProps) => {
    const {
        isOpen,
        values,
        preferencesBasis,
        regionDetails,
        homeMatchBasicConsent,
        isSaving,
        onHomeMatchBasicConsentChange,
        onEditPreferences,
        onConfirm,
        onClose,
        trackingContext,
        zIndex
    } = props;
    const titleId = useId();
    const descriptionId = useId();
    const homeMatchTitleId = useId();
    const homeMatchSwitcherId = useId();
    const {isMobile} = useUserDevice();
    const {handleClose, handleConfirm, handleEditPreferences, handleHomeMatchBasicConsentChange} = useHomeMatchBasicSummaryGtmTracking({
        isOpen,
        homeMatchBasicConsent,
        trackingContext,
        onClose,
        onConfirm,
        onEditPreferences,
        onHomeMatchBasicConsentChange
    });

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={handleClose}
            zIndex={zIndex}
            ariaLabelledBy={titleId}
            ariaDescribedBy={descriptionId}
            className={modalStyle}
            overlayClassName={overlayStyle}
            dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.WRAPPER}
        >
            <ResponsiveModal.Header divider={false} className={headerStyle}>
                <img src={buildingsImage} alt="" width={72} height={72} className={illustrationStyle} />

                <div className={headerTextStyle}>
                    <Text as="h2" id={titleId} variant="headline_4" color="var(--colors-secondary)">
                        Przeprowadzimy Cię przez dalsze poszukiwania
                    </Text>
                    <Text as="p" id={descriptionId} variant="body_copy_2" color="var(--colors-secondary)" className={bodyTextStyle}>
                        {getHomeMatchBasicPreferencesBasisText(preferencesBasis)}
                    </Text>
                </div>

                <ResponsiveModal.CloseButton dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.CLOSE_BUTTON} />
            </ResponsiveModal.Header>

            <ResponsiveModal.Body className={bodyStyle}>
                <section className={preferencesCardStyle}>
                    <div className={cardTextStyle}>
                        <Text as="h3" variant="headline_6" color="var(--colors-secondary)" className={cardTitleStyle}>
                            Twoje preferencje
                        </Text>
                        <Text
                            as="p"
                            variant="body_copy_2"
                            color="var(--colors-gray-700)"
                            className={bodyTextStyle}
                            dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.PREFERENCES_TEXT}
                        >
                            {getHomeMatchBasicPreferencesSummaryText(values, regionDetails)}
                        </Text>
                    </div>

                    <Button
                        type="button"
                        variant="outlined_secondary"
                        size="x-small"
                        iconLeft={EditIcon}
                        className={editButtonStyle}
                        onClick={handleEditPreferences}
                        dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.EDIT_PREFERENCES_BUTTON}
                    >
                        ZMIEŃ
                    </Button>
                </section>

                <section className={homeMatchCardStyle}>
                    <div className={cardTextStyle}>
                        <Text as="h3" id={homeMatchTitleId} variant="headline_6" color="var(--colors-secondary)" className={cardTitleStyle}>
                            Dopasujemy oferty dla Ciebie
                        </Text>
                        <Text as="p" variant="body_copy_2" color="var(--colors-gray-700)" className={bodyTextStyle}>
                            Włącz tę opcję, a dzięki niej:
                        </Text>
                        <ul className={benefitListStyle}>
                            {HOME_MATCH_BASIC_BENEFITS.map((benefit) => (
                                <li key={benefit} className={benefitItemStyle}>
                                    <span className={benefitIconStyle}>
                                        <CheckIcon size="1" fill="var(--colors-secondary)" />
                                    </span>
                                    <Text as="p" variant="body_copy_2" color="var(--colors-gray-700)" className={cx(bodyTextStyle, benefitTextStyle)}>
                                        {benefit}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <Switcher
                        id={homeMatchSwitcherId}
                        checked={homeMatchBasicConsent}
                        checkedColor="var(--colors-secondary)"
                        variant={isMobile === false ? "big" : "small"}
                        onChange={handleHomeMatchBasicConsentChange}
                        aria-labelledby={homeMatchTitleId}
                        wrapperClassName={switcherWrapperStyle}
                        dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.AUTO_MATCH_SWITCH}
                    />
                </section>
            </ResponsiveModal.Body>

            <ResponsiveModal.Footer className={footerStyle}>
                <Button
                    type="button"
                    variant="filled_primary"
                    size="medium"
                    fullWidth
                    disabled={isSaving}
                    onClick={handleConfirm}
                    dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.CONFIRM_BUTTON}
                >
                    {isSaving ? "ZAPISYWANIE..." : "Zapisz i zobacz oferty"}
                </Button>
            </ResponsiveModal.Footer>
        </ResponsiveModal>
    );
};

const modalStyle = css({
    md: {
        borderRadius: 2
    }
});

// Opened in place of a previous modal of the registration flow - the backdrop stays fully visible instead of fading in again
const overlayStyle = css({
    animation: "none"
});

const headerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5
});

const illustrationStyle = css({
    display: "block",
    flexShrink: 0,
    width: "7.2rem",
    height: "7.2rem"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0
});

const bodyTextStyle = css({
    lineHeight: "1.8rem",
    md: {
        lineHeight: "2rem"
    }
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    px: "1.8rem",
    md: {
        px: "2.3rem"
    }
});

const cardStyle = css.raw({
    display: "flex",
    columnGap: 2,
    border: "1px solid var(--colors-gray-400)",
    borderRadius: 1,
    p: 1.5
});

const preferencesCardStyle = css({
    ...cardStyle,
    alignItems: "center"
});

const homeMatchCardStyle = css({
    ...cardStyle,
    alignItems: "center",
    md: {
        alignItems: "flex-start"
    }
});

const cardTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0
});

const cardTitleStyle = css({
    fontWeight: 700,
    md: {
        fontWeight: 500
    }
});

const editButtonStyle = css({
    flexShrink: 0
});

const benefitListStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    listStyle: "none",
    m: 0,
    p: 0
});

const benefitItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: 1
});

const benefitIconStyle = css({
    display: "flex",
    flexShrink: 0,
    pt: 0.5
});

const benefitTextStyle = css({
    flex: "1 1 auto",
    minWidth: 0
});

const switcherWrapperStyle = css({
    flexShrink: 0,
    mr: "-0.3rem",
    md: {
        mt: "-0.3rem"
    }
});

const footerStyle = css({
    px: 2,
    pt: 1.5,
    pb: 3
});
import {useId} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon, EditIcon} from "@pg-design/icons-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {Switcher} from "@pg-design/switcher-module";
import {Text} from "@pg-design/text-module";
import {HOME_MATCH_BASIC_SUMMARY_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import type {IRegionListRegion} from "../../../search/actions/fetch_search_regions_action";
import {useHomeMatchBasicSummaryGtmTracking} from "../../hooks/use_home_match_basic_summary_gtm_tracking";
import type {HomeMatchBasicPreferencesBasis, IHomeMatchBasicPreferencesFlowTrackingContext} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import type {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {getHomeMatchBasicPreferencesBasisText, getHomeMatchBasicPreferencesSummaryText} from "../../utils/home_match_basic_preferences_flow_utils";

const buildingsImage = require("../../images/home_match_basic_buildings.svg");

const HOME_MATCH_BASIC_BENEFITS = [
    "Automatycznie wysyłamy zapytania do deweloperów w Twoim imieniu.",
    "Deweloperzy sami wracają do Ciebie z dopasowanymi ofertami.",
    "O najlepszych okazjach informujemy Cię od razu SMS-em i e-mailem."
];

interface IProps {
    isOpen: boolean;
    values: IPreferencesDefaultValues;
    preferencesBasis: HomeMatchBasicPreferencesBasis;
    regionDetails?: IRegionListRegion[];
    homeMatchBasicConsent: boolean;
    isSaving: boolean;
    onHomeMatchBasicConsentChange: (value: boolean) => void;
    onEditPreferences: () => void;
    onConfirm: () => void;
    onClose: () => void;
    trackingContext?: IHomeMatchBasicPreferencesFlowTrackingContext;
    zIndex?: number;
}

export const HomeMatchBasicPreferencesFlowSummaryResponsiveModal = (props: IProps) => {
    const {
        isOpen,
        values,
        preferencesBasis,
        regionDetails,
        homeMatchBasicConsent,
        isSaving,
        onHomeMatchBasicConsentChange,
        onEditPreferences,
        onConfirm,
        onClose,
        trackingContext,
        zIndex
    } = props;
    const titleId = useId();
    const descriptionId = useId();
    const homeMatchTitleId = useId();
    const homeMatchSwitcherId = useId();
    const {isMobile} = useUserDevice();
    const {handleClose, handleConfirm, handleEditPreferences, handleHomeMatchBasicConsentChange} = useHomeMatchBasicSummaryGtmTracking({
        isOpen,
        homeMatchBasicConsent,
        trackingContext,
        onClose,
        onConfirm,
        onEditPreferences,
        onHomeMatchBasicConsentChange
    });

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={handleClose}
            zIndex={zIndex}
            ariaLabelledBy={titleId}
            ariaDescribedBy={descriptionId}
            className={modalStyle}
            overlayClassName={overlayStyle}
            dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.WRAPPER}
        >
            <ResponsiveModal.Header divider={false} className={headerStyle}>
                <img src={buildingsImage} alt="" width={72} height={72} className={illustrationStyle} />

                <div className={headerTextStyle}>
                    <Text as="h2" id={titleId} variant="headline_4" color="var(--colors-secondary)">
                        Przeprowadzimy Cię przez dalsze poszukiwania
                    </Text>
                    <Text as="p" id={descriptionId} variant="body_copy_2" color="var(--colors-secondary)" className={bodyTextStyle}>
                        {getHomeMatchBasicPreferencesBasisText(preferencesBasis)}
                    </Text>
                </div>

                <ResponsiveModal.CloseButton dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.CLOSE_BUTTON} />
            </ResponsiveModal.Header>

            <ResponsiveModal.Body className={bodyStyle}>
                <section className={preferencesCardStyle}>
                    <div className={cardTextStyle}>
                        <Text as="h3" variant="headline_6" color="var(--colors-secondary)" className={cardTitleStyle}>
                            Twoje preferencje
                        </Text>
                        <Text
                            as="p"
                            variant="body_copy_2"
                            color="var(--colors-gray-700)"
                            className={bodyTextStyle}
                            dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.PREFERENCES_TEXT}
                        >
                            {getHomeMatchBasicPreferencesSummaryText(values, regionDetails)}
                        </Text>
                    </div>

                    <Button
                        type="button"
                        variant="outlined_secondary"
                        size="x-small"
                        iconLeft={EditIcon}
                        className={editButtonStyle}
                        onClick={handleEditPreferences}
                        dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.EDIT_PREFERENCES_BUTTON}
                    >
                        ZMIEŃ
                    </Button>
                </section>

                <section className={homeMatchCardStyle}>
                    <div className={cardTextStyle}>
                        <Text as="h3" id={homeMatchTitleId} variant="headline_6" color="var(--colors-secondary)" className={cardTitleStyle}>
                            Dopasujemy oferty dla Ciebie
                        </Text>
                        <Text as="p" variant="body_copy_2" color="var(--colors-gray-700)" className={bodyTextStyle}>
                            Włącz tę opcję, a dzięki niej:
                        </Text>
                        <ul className={benefitListStyle}>
                            {HOME_MATCH_BASIC_BENEFITS.map((benefit) => (
                                <li key={benefit} className={benefitItemStyle}>
                                    <span className={benefitIconStyle}>
                                        <CheckIcon size="1" fill="var(--colors-secondary)" />
                                    </span>
                                    <Text as="p" variant="body_copy_2" color="var(--colors-gray-700)" className={cx(bodyTextStyle, benefitTextStyle)}>
                                        {benefit}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <Switcher
                        id={homeMatchSwitcherId}
                        checked={homeMatchBasicConsent}
                        checkedColor="var(--colors-secondary)"
                        variant={isMobile === false ? "big" : "small"}
                        onChange={handleHomeMatchBasicConsentChange}
                        aria-labelledby={homeMatchTitleId}
                        wrapperClassName={switcherWrapperStyle}
                        dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.AUTO_MATCH_SWITCH}
                    />
                </section>
            </ResponsiveModal.Body>

            <ResponsiveModal.Footer className={footerStyle}>
                <Button
                    type="button"
                    variant="filled_primary"
                    size="medium"
                    fullWidth
                    disabled={isSaving}
                    onClick={handleConfirm}
                    dataTestId={HOME_MATCH_BASIC_SUMMARY_TEST_IDS.CONFIRM_BUTTON}
                >
                    {isSaving ? "ZAPISYWANIE..." : "Zapisz i zobacz oferty"}
                </Button>
            </ResponsiveModal.Footer>
        </ResponsiveModal>
    );
};

const modalStyle = css({
    md: {
        borderRadius: 2
    }
});

// Opened in place of a previous modal of the registration flow - the backdrop stays fully visible instead of fading in again
const overlayStyle = css({
    animation: "none"
});

const headerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5
});

const illustrationStyle = css({
    display: "block",
    flexShrink: 0,
    width: "7.2rem",
    height: "7.2rem"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0
});

const bodyTextStyle = css({
    lineHeight: "1.8rem",
    md: {
        lineHeight: "2rem"
    }
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    px: "1.8rem",
    md: {
        px: "2.3rem"
    }
});

const cardStyle = css.raw({
    display: "flex",
    columnGap: 2,
    border: "1px solid var(--colors-gray-400)",
    borderRadius: 1,
    p: 1.5
});

const preferencesCardStyle = css({
    ...cardStyle,
    alignItems: "center"
});

const homeMatchCardStyle = css({
    ...cardStyle,
    alignItems: "center",
    md: {
        alignItems: "flex-start"
    }
});

const cardTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0
});

const cardTitleStyle = css({
    fontWeight: 700,
    md: {
        fontWeight: 500
    }
});

const editButtonStyle = css({
    flexShrink: 0
});

const benefitListStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    listStyle: "none",
    m: 0,
    p: 0
});

const benefitItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: 1
});

const benefitIconStyle = css({
    display: "flex",
    flexShrink: 0,
    pt: 0.5
});

const benefitTextStyle = css({
    flex: "1 1 auto",
    minWidth: 0
});

const switcherWrapperStyle = css({
    flexShrink: 0,
    mr: "-0.3rem",
    md: {
        mt: "-0.3rem"
    }
});

const footerStyle = css({
    px: 2,
    pt: 1.5,
    pb: 3
});
